/* tyt-search-clear.js — the clear "x" inside every search box.
 *
 * WHY THIS EXISTS AT ALL. Every search box on the site is `type="search"`,
 * which is *supposed* to give a native clear button. Measured on 2026-09-16,
 * after Maud reported it on her iPad: it appears NOWHERE. iOS Safari never
 * draws one, Android Chrome never draws one, and desktop Chrome did not draw
 * one either, focused or unfocused. So a visitor who types a query has no way
 * to get rid of it except holding backspace.
 *
 * This is the same conclusion as tyt-faq-rail.js on 2026-09-09, and the same
 * rule: ANYTHING WHOSE VISIBILITY IS DECIDED BY THE BROWSER'S OWN UI —
 * scrollbars, focus rings, input chrome, select popups — has to be replaced
 * with an element we own, or it will be invisible on the device that matters.
 * The CSS also suppresses ::-webkit-search-cancel-button, so a browser that
 * DOES draw its own cannot double up with ours.
 *
 * FOUR THINGS THAT ARE LOAD-BEARING:
 *
 * 1. IT DISPATCHES `input`, NOT JUST `value = ''`. Setting an input's value in
 *    script fires no event. Four separate filters listen for `input` on these
 *    boxes — tyt-collection-filter.js, tyt-faq-filter.js, tyt-blog-filter.js
 *    and tyt-walks-filter.js — so without the dispatch the box would empty and
 *    the results would stay filtered. That is worse than no button.
 *
 * 2. `type="button"`. Three of these boxes sit inside a real <form> (the header
 *    search, the search page, the FAQ bar). A button with no type is a SUBMIT
 *    button, so clearing would navigate.
 *
 * 3. IT IS INSERTED AS A FLEX SIBLING, immediately after the input. Every
 *    wrapper on this site — .tyt-walks-search, .tyt-faq-search,
 *    .tyt-walks-dd__search, .tyt-search__box — is a flex row of
 *    [icon, input, (submit)], so the x lands between the input and any submit
 *    button with no absolute positioning and nothing to keep in sync.
 *
 * 4. IT RUNS ON BOXES THAT DO NOT EXIST YET. The Our Walks facet dropdowns and
 *    the predictive panel are built or revealed after load, so a MutationObserver
 *    picks up any input[type=search] added later. Attaching twice is guarded by
 *    a flag on the input.
 *
 * DELIBERATELY NOT DONE: clearing does not re-run a server-side search. On
 * /search the box empties and the visitor presses Search (or Return) — the same
 * as deleting the text by hand. Firing a navigation off a clear would be a
 * surprise.
 *
 * ⚠ ONE BOX OPTS OUT: THE HEADER SEARCH PANEL (`data-tyt-no-clear`). That row
 * already ends in a bare "×" that CLOSES the panel, so adding ours put two
 * crosses about ten pixels apart doing two different things. Maud, 2026-09-16:
 * *"the generic search now somehow has 2 'x' - one in the search bar and one
 * behind the search button."* Two adjacent crosses with different meanings is
 * worse than no clear button at all, and the panel's own × is the one a visitor
 * reaches for anyway. If the header ever loses that close button, drop the
 * attribute and the clear × comes back on its own.
 */
(function () {
  'use strict';

  if (window.tytSearchClear) return; /* the script is loaded once, from the layout */

  var LABEL = 'Clear search';
  var ICON =
    '<svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" focusable="false">' +
    '<path d="M3.5 3.5l9 9M12.5 3.5l-9 9" fill="none" stroke="currentColor" ' +
    'stroke-width="1.7" stroke-linecap="round"/></svg>';

  function attach(input) {
    if (input._tytClearOn) return;
    if (input.hasAttribute('data-tyt-no-clear')) return;
    var parent = input.parentNode;
    if (!parent) return;
    input._tytClearOn = true;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tyt-search-clear';
    btn.setAttribute('aria-label', LABEL);
    btn.title = LABEL;
    btn.innerHTML = ICON;
    btn.hidden = true;

    if (input.nextSibling) parent.insertBefore(btn, input.nextSibling);
    else parent.appendChild(btn);

    function sync() { btn.hidden = input.value.length === 0; }

    input.addEventListener('input', sync);
    input.addEventListener('change', sync);

    btn.addEventListener('click', function (e) {
      /* The wrapper is often a <label>. A click on an interactive descendant is
       * not forwarded to the control by the spec, but stopping it costs nothing
       * and keeps the header's form out of it. */
      e.preventDefault();
      e.stopPropagation();
      if (!input.value) return;
      input.value = '';
      sync();
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      /* Keep the keyboard up on a phone: clearing almost always means
       * "I want to type something else", not "I am done here". */
      input.focus();
    });

    sync();
  }

  function scan(root) {
    var boxes = (root || document).querySelectorAll('input[type="search"]');
    for (var i = 0; i < boxes.length; i++) attach(boxes[i]);
  }

  function boot() {
    scan();
    if (!window.MutationObserver) return;
    /* Debounced: the collection filter and the shuffle grid both add or remove
     * many children at once, and a full-document querySelectorAll per mutation
     * record would be pure waste. childList only — an attribute change (the
     * inline styles tyt-grid-flow.js writes) can never add a search box. */
    var t;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(function () { scan(); }, 100);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });

  window.tytSearchClear = { scan: scan };
})();
